import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { useSiteImages } from "@/hooks/useSiteImages";
import { Marquee } from "@/components/Marquee";
import { TalentCard } from "@/components/TalentCard";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1613915617430-8ab0fd7c6baf?crop=entropy&cs=srgb&fm=jpg&q=85";
const DEFAULT_MONTREAL = "https://images.unsplash.com/photo-1616034328430-a1ec65cabb01?crop=entropy&cs=srgb&fm=jpg&q=85";
const DEFAULT_CAMPAIGNS = [
  { img: "https://images.unsplash.com/photo-1571513800374-df1bbe650e56?crop=entropy&cs=srgb&fm=jpg&q=85", title: "Editorial Series 001", tag: "Fashion Editorial" },
  { img: "https://images.unsplash.com/photo-1662532577856-e8ee8b138a8b?crop=entropy&cs=srgb&fm=jpg&q=85", title: "Screen Test 002", tag: "Film & TV" },
  { img: "https://images.unsplash.com/photo-1613915617430-8ab0fd7c6baf?crop=entropy&cs=srgb&fm=jpg&q=85", title: "Portrait Study 003", tag: "Campaign" },
  { img: "https://images.unsplash.com/flagged/photo-1570733117311-d990c3816c47?crop=entropy&cs=srgb&fm=jpg&q=85", title: "Studio Session 004", tag: "Commercial" },
];
const DEFAULT_DIV_IMGS = {
  models: "https://images.unsplash.com/photo-1516575150278-77136aed6920?crop=entropy&cs=srgb&fm=jpg&q=85",
  actors: "https://images.unsplash.com/photo-1654765437547-6b572f52ee1a?crop=entropy&cs=srgb&fm=jpg&q=85",
  voice: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const CityClock = ({ zone, label, feature = false }) => {
  const [time, setTime] = useState("--:--");
  useEffect(() => {
    const tick = () =>
      setTime(
        new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: zone }).format(new Date())
      );
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [zone]);
  return (
    <span className={`font-mono uppercase tracking-[0.25em] ${feature ? "text-xs text-white/80 sm:text-sm" : "text-[10px] text-white/45 sm:text-xs"}`}>
      {label} <span className="text-gold">{time}</span>
    </span>
  );
};

export default function Home() {
  const { t } = useLanguage();
  const img = useSiteImages();
  useSEO("Diamotims Agency — Talent Management, Montréal", t("home.heroSub"));
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const HERO_IMG = img("hero", DEFAULT_HERO);
  const MONTREAL_IMG = img("home_montreal", DEFAULT_MONTREAL);
  const CAMPAIGNS = DEFAULT_CAMPAIGNS.map((c, i) => ({ ...c, img: img(`campaign_${i + 1}`, c.img) }));
  const DIVISIONS = [
    { to: "/models", title: t("footer.models"), copy: t("home.divModelsCopy"), img: img("div_models", DEFAULT_DIV_IMGS.models), testid: "division-card-models" },
    { to: "/actors", title: t("footer.actors"), copy: t("home.divActorsCopy"), img: img("div_actors", DEFAULT_DIV_IMGS.actors), testid: "division-card-actors" },
    { to: "/voice", title: t("footer.voice"), copy: t("home.divVoiceCopy"), img: img("div_voice", DEFAULT_DIV_IMGS.voice), testid: "division-card-voice" },
  ];

  useEffect(() => {
    api.get("/talent", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <section ref={heroRef} data-testid="hero-section" className="relative flex min-h-screen flex-col justify-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          {HERO_IMG && (
            <motion.img
              initial={{ scale: 1.15, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
              src={HERO_IMG}
              alt="Diamotims Agency — campaign portrait"
              className="h-full w-full object-cover object-top"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/55 to-[#0A0A0A]/30" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1800px] px-4 pb-10 pt-32 sm:px-8 lg:px-16">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mb-6 font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs"
          >
            {t("home.eyebrow")}
          </motion.p>

          <h1 className="font-display text-[11vw] font-light uppercase leading-[0.95] tracking-tight text-ivory sm:text-8xl lg:text-[8.5rem]">
            <MaskedLine delay={0.25}>{t("home.hero1")}</MaskedLine>
            <MaskedLine delay={0.37}>{t("home.hero2")}</MaskedLine>
            <MaskedLine delay={0.49}>
              <span className="italic text-gold">{t("home.hero3")}</span>
            </MaskedLine>
          </h1>

          <div className="mt-10 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8 }}
              className="max-w-xl font-display text-xl font-light italic leading-relaxed text-white/80 sm:text-2xl"
            >
              {t("home.heroSub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/discover"
                data-testid="hero-discover-button"
                className="group flex items-center gap-3 bg-ivory px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-gold"
              >
                {t("home.discover")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
              <Link
                to="/clients"
                data-testid="hero-work-with-us-button"
                className="flex items-center gap-3 border border-white/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                {t("home.workWithUs")}
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/15 pt-5"
          >
            <CityClock zone="America/Toronto" label="Montréal" feature />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">45.5017° N, 73.5673° W</span>
            <span className="hidden h-3 w-px bg-white/15 sm:block" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45 sm:text-xs">{t("home.oneHouse")}</span>
            <span className="ml-auto hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 sm:flex">
              {t("home.scroll")} <ArrowDown className="h-3 w-3 animate-bounce" />
            </span>
          </motion.div>
        </motion.div>
      </section>

      <Marquee items={["Fashion", "Film", "Television", "Commercial", "Voice-Over", "Animation", "Audiobooks", "Theatre", "Video Games"]} />

      <section className="mx-auto max-w-[1800px] px-4 py-20 sm:px-8 lg:px-16 lg:py-32">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <FadeUp>
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">{t("home.featuredEyebrow")}</div>
            <h2 className="mt-4 font-display text-3xl font-light uppercase leading-[1.02] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
              {t("home.featuredTitle")}
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Link to="/discover" data-testid="home-view-all-talent" className="group flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-white/60 transition-colors hover:text-gold">
              {t("home.discoverAll")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </FadeUp>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {featured.map((talentItem, i) => (
            <TalentCard key={talentItem.id} talent={talentItem} index={i} />
          ))}
        </div>
      </section>

      <section className="border-t border-white/10">
        <div className="mx-auto max-w-[1800px] px-4 py-20 sm:px-8 lg:px-16 lg:py-32">
          <FadeUp>
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">{t("home.divisionsEyebrow")}</div>
            <h2 className="mt-4 max-w-3xl font-display text-3xl font-light uppercase leading-[1.02] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
              {t("home.divisionsTitle")}
            </h2>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {DIVISIONS.map((d, i) => (
              <FadeUp key={d.to} delay={i * 0.12}>
                <Link to={d.to} data-testid={d.testid} className="group block" data-cursor>
                  <div className="relative aspect-[3/4] overflow-hidden border border-white/10">
                    {(d.to === "/actors" || d.to === "/voice") && (
                      <span className="absolute right-4 top-4 z-10 border border-gold/50 bg-black/60 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-gold backdrop-blur-md">
                        {t("soon.title")}
                      </span>
                    )}
                    {d.img ? (
                      <img src={d.img} alt={d.title} loading="lazy" className="h-full w-full object-cover grayscale-[45%] transition-[transform,filter] duration-700 group-hover:scale-[1.05] group-hover:grayscale-0" />
                    ) : (
                      <div className="h-full w-full animate-pulse bg-white/5" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t("home.divisionLabel")} 0{i + 1}</div>
                      <h3 className="mt-2 font-display text-3xl font-light uppercase tracking-tight text-white lg:text-4xl">{d.title}</h3>
                      <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/60">{d.copy}</p>
                      <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/70 transition-colors group-hover:text-gold">
                        {t("home.enter")} <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10">
        <div className="mx-auto max-w-[1800px] px-4 py-20 sm:px-8 lg:px-16 lg:py-32">
          <FadeUp>
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">{t("home.campaignsEyebrow")}</div>
            <h2 className="mt-4 font-display text-3xl font-light uppercase leading-[1.02] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
              {t("home.campaignsTitle")}
            </h2>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {CAMPAIGNS.map((c, i) => (
              <FadeUp key={c.title} delay={i * 0.1}>
                <figure className="group" data-cursor>
                  <div className="overflow-hidden border border-white/10">
                    {c.img ? (
                      <img src={c.img} alt={c.title} loading="lazy" className="aspect-[3/4] w-full object-cover grayscale-[35%] transition-[transform,filter] duration-700 group-hover:scale-[1.04] group-hover:grayscale-0" />
                    ) : (
                      <div className="aspect-[3/4] w-full animate-pulse bg-white/5" />
                    )}
                  </div>
                  <figcaption className="mt-3 flex items-baseline justify-between">
                    <span className="font-syne text-sm font-semibold uppercase tracking-wide text-white">{c.title}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold">{c.tag}</span>
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10">
        <div className="mx-auto grid max-w-[1800px] grid-cols-1 items-center gap-10 px-4 py-20 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-16 lg:py-32">
          <FadeUp className="lg:col-span-5">
            <div className="overflow-hidden border border-white/10">
              {MONTREAL_IMG ? (
                <img src={MONTREAL_IMG} alt="Montréal, Québec" loading="lazy" className="aspect-[4/5] w-full object-cover grayscale-[30%] transition-[transform,filter] duration-700 hover:scale-[1.03] hover:grayscale-0" />
              ) : (
                <div className="aspect-[4/5] w-full animate-pulse bg-white/5" />
              )}
            </div>
          </FadeUp>
          <div className="lg:col-span-7">
            <FadeUp>
              <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">{t("home.philosophyEyebrow")}</div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-6 font-display text-2xl font-light italic leading-snug text-white/85 sm:text-4xl lg:text-[2.75rem]">
                {t("home.quote")}
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-8 max-w-xl text-sm leading-relaxed text-white/60 sm:text-base">
                {t("home.philosophyBody")}
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <Link to="/about" data-testid="home-about-link" className="group mt-10 inline-flex items-center gap-3 border border-white/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:border-gold hover:text-gold">
                {t("home.aboutLink")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      <Link to="/submissions" data-testid="home-cta-submissions" className="group relative block overflow-hidden border-t border-white/10">
        <div className="mx-auto flex max-w-[1800px] items-center justify-between gap-6 px-4 py-16 sm:px-8 lg:px-16 lg:py-24">
          <h2 className="font-display text-4xl font-light uppercase tracking-tight text-white transition-colors duration-500 group-hover:text-gold sm:text-6xl lg:text-8xl">
            {t("home.ctaBand")}
          </h2>
          <ArrowRight className="h-10 w-10 shrink-0 text-white transition-all duration-500 group-hover:translate-x-3 group-hover:text-gold sm:h-16 sm:w-16" strokeWidth={1.25} />
        </div>
      </Link>
    </main>
  );
}
